/**
 * InstallAppModal Component
 *
 * Modal for installing a marketplace application with configuration options.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import type { MarketplaceListing, MarketplaceInstallDTO } from '../../../types/marketplace.types';
import { useInstallMarketplaceApp } from '../hooks/useInstallMarketplaceApp';
import { getErrorMessage } from '../../../lib/utils/errors';

interface InstallAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  listing: MarketplaceListing;
  tenantId: string;
}

/**
 * InstallAppModal component
 */
export const InstallAppModal: React.FC<InstallAppModalProps> = ({
  isOpen,
  onClose,
  listing,
  tenantId,
}) => {
  const latestVersion = listing.availableVersions?.[listing.availableVersions.length - 1];

  const [applicationCode, setApplicationCode] = useState(
    listing.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').slice(0, 32)
  );
  const [confirmationCode, setConfirmationCode] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(latestVersion?.version || '');
  const [error, setError] = useState<string | null>(null);

  const installMutation = useInstallMarketplaceApp();

  const handleInstall = async () => {
    setError(null);

    if (!applicationCode.trim()) {
      setError('Application code is required');
      return;
    }

    if (!selectedVersion) {
      setError('Please select a version');
      return;
    }

    if (confirmationCode.trim() !== applicationCode.trim()) {
      setError('Confirmation code must match the application code');
      return;
    }

    const data: MarketplaceInstallDTO = {
      listingId: listing.listingId,
      applicationCode: applicationCode.trim(),
      confirmationCode: confirmationCode.trim(),
      version: selectedVersion,
    };

    try {
      await installMutation.mutateAsync({ tenantId, data });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Install ${listing.name}`}
      size="md"
    >
      <div className="space-y-4">
        {/* App Info */}
        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
          {listing.iconUrl ? (
            <img
              src={listing.iconUrl}
              alt={listing.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-semibold">
                {listing.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{listing.name}</p>
            <p className="text-sm text-gray-500">by {listing.publisherTenantCode}</p>
          </div>
        </div>

        {/* Version Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Version
          </label>
          <select
            value={selectedVersion}
            onChange={(e) => setSelectedVersion(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
          >
            {listing.availableVersions?.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version} {v === latestVersion ? '(latest)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Application Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Application Code *
          </label>
          <Input
            value={applicationCode}
            onChange={(e) => setApplicationCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="my-app"
            maxLength={32}
          />
          <p className="mt-1 text-xs text-gray-500">
            Unique identifier for this application (lowercase letters, numbers, hyphens)
          </p>
        </div>

        {/* Confirmation Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Application Code *
          </label>
          <Input
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="Type the application code again to confirm"
            maxLength={32}
          />
          <p className="mt-1 text-xs text-gray-500">
            Type <span className="font-mono font-medium">{applicationCode || 'the code'}</span> to confirm installation
          </p>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleInstall}
            disabled={installMutation.isPending}
          >
            {installMutation.isPending ? 'Installing...' : 'Install Application'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default InstallAppModal;
